'use client'

import React, { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Loader2,
  DollarSign,
  X,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Clock,
  Zap,
  AlertCircle
} from 'lucide-react'
import Link from 'next/link'
import { useNexusBackground } from '@/providers/nexus-background-provider'

/**
 * NexusGlobalStatusBar
 *
 * Displays active Nexus background tasks in the header.
 * Shows:
 * - Number of active tasks with status
 * - Real-time cost animation
 * - Individual task chips with cancel buttons
 * - Expandable details panel
 */
export function NexusGlobalStatusBar() {
  const {
    activeTasks,
    hasActiveTasks,
    totalCost,
    maxProjectCost,
    lifetimeCost,
    runningCount,
    pendingCount,
    cancelTask
  } = useNexusBackground()

  const [isExpanded, setIsExpanded] = useState(false)
  const [previousCost, setPreviousCost] = useState(0)

  // Track cost changes for animation
  useEffect(() => {
    if (totalCost !== previousCost) {
      setPreviousCost(totalCost)
    }
  }, [totalCost, previousCost])

  // Don't render if no active tasks
  if (!hasActiveTasks) {
    return null
  }

  // Group tasks by status
  const runningTasks = activeTasks.filter(t => t.status === 'running')
  const pendingTasks = activeTasks.filter(t => t.status === 'pending' || t.status === 'claimed')

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white shadow-lg"
      >
        {/* Main bar */}
        <div className="px-4 py-2 flex items-center justify-between">
          {/* Left: Status indicator */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <Loader2 className="w-4 h-4 animate-spin" />
              {runningCount > 0 && (
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              )}
            </div>

            <span className="font-medium text-sm">
              {runningCount > 0 ? (
                <>
                  {runningCount} Nexus Task{runningCount > 1 ? 's' : ''} aktiv
                  {pendingCount > 0 && (
                    <span className="text-white/70 ml-1">
                      (+{pendingCount} wartend)
                    </span>
                  )}
                </>
              ) : (
                <>
                  {pendingCount} Task{pendingCount > 1 ? 's' : ''} in Warteschlange
                </>
              )}
            </span>
          </div>

          {/* Center: Cost display with animation - aktuell / projekt / gesamt */}
          <div className="flex items-center gap-4">
            {/* Aktuell - current session cost */}
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-yellow-300" />
              <motion.span
                key={totalCost.toFixed(4)}
                initial={{ scale: 1.3, color: '#fde047' }}
                animate={{ scale: 1, color: '#ffffff' }}
                transition={{ duration: 0.3 }}
                className="font-mono text-sm font-bold"
              >
                ${totalCost.toFixed(4)}
              </motion.span>
              <span className="text-xs text-white/60">aktuell</span>
            </div>
            {/* Projekt - project total + current session */}
            {maxProjectCost > 0 && (
              <div className="flex items-center gap-1 text-white/70">
                <span className="font-mono text-xs">${(maxProjectCost + totalCost).toFixed(2)}</span>
                <span className="text-xs text-white/50">projekt</span>
              </div>
            )}
            {/* Gesamt - lifetime total + current session */}
            {lifetimeCost > 0 && (
              <div className="flex items-center gap-1 text-white/70">
                <span className="font-mono text-xs">${(lifetimeCost + totalCost).toFixed(2)}</span>
                <span className="text-xs text-white/50">gesamt</span>
              </div>
            )}
          </div>

          {/* Right: Task chips + expand toggle */}
          <div className="flex items-center gap-2">
            {/* Show first 3 tasks as chips */}
            <div className="hidden md:flex items-center gap-2">
              {activeTasks.slice(0, 3).map(task => (
                <TaskChip
                  key={task.task_id}
                  task={task}
                  onCancel={() => cancelTask(task.task_id)}
                />
              ))}
              {activeTasks.length > 3 && (
                <span className="text-xs text-white/70">
                  +{activeTasks.length - 3} mehr
                </span>
              )}
            </div>

            {/* Mobile: Just show count */}
            <div className="md:hidden flex items-center gap-2">
              <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                {activeTasks.length} Task{activeTasks.length > 1 ? 's' : ''}
              </span>
            </div>

            {/* Expand toggle */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 hover:bg-white/10 rounded transition-colors"
              title={isExpanded ? 'Details ausblenden' : 'Details anzeigen'}
            >
              {isExpanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        {/* Expanded details panel */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-white/10 bg-black/20 overflow-hidden"
            >
              <div className="px-4 py-3 space-y-2">
                {/* Running tasks */}
                {runningTasks.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-3 h-3 text-green-400" />
                      <span className="text-xs font-medium text-white/70 uppercase">
                        Aktiv
                      </span>
                    </div>
                    <div className="space-y-1">
                      {runningTasks.map(task => (
                        <TaskRow key={task.task_id} task={task} onCancel={cancelTask} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Pending tasks */}
                {pendingTasks.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-3 h-3 text-yellow-400" />
                      <span className="text-xs font-medium text-white/70 uppercase">
                        Wartend
                      </span>
                    </div>
                    <div className="space-y-1">
                      {pendingTasks.map(task => (
                        <TaskRow key={task.task_id} task={task} onCancel={cancelTask} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Link to developer page */}
                <div className="pt-2 border-t border-white/10">
                  <Link
                    href="/developer"
                    className="flex items-center gap-1 text-xs text-white/70 hover:text-white transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Alle Projekte anzeigen
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  )
}

/**
 * TaskChip - Compact task display for the main bar
 */
function TaskChip({ task, onCancel }) {
  const [isHovered, setIsHovered] = useState(false)

  const statusColors = {
    running: 'bg-green-500/20 border-green-400/50',
    pending: 'bg-yellow-500/20 border-yellow-400/50',
    claimed: 'bg-yellow-500/20 border-yellow-400/50'
  }

  return (
    <motion.div
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`
        relative flex items-center gap-2 px-2 py-1 rounded-full border text-xs
        ${statusColors[task.status] || 'bg-white/10 border-white/20'}
        transition-colors
      `}
    >
      {/* Status indicator */}
      {task.status === 'running' && (
        <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
      )}

      {/* Project name */}
      <span className="max-w-[100px] truncate">
        {task.project_name || `Project ${task.project_id}`}
      </span>

      {/* Progress if running */}
      {task.status === 'running' && task.progress_percent > 0 && (
        <span className="text-white/70">{task.progress_percent}%</span>
      )}

      {/* Cancel button on hover */}
      <AnimatePresence>
        {isHovered && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={(e) => {
              e.stopPropagation()
              onCancel()
            }}
            className="absolute -right-1 -top-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
            title="Abbrechen"
          >
            <X className="w-2.5 h-2.5" />
          </motion.button>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

/**
 * TaskRow - Detailed task display for expanded panel
 */
function TaskRow({ task, onCancel }) {
  const statusConfig = {
    running: { icon: Loader2, color: 'text-green-400', label: 'Läuft' },
    pending: { icon: Clock, color: 'text-yellow-400', label: 'Wartend' },
    claimed: { icon: Zap, color: 'text-yellow-400', label: 'Startet' }
  }

  const config = statusConfig[task.status] || statusConfig.pending
  const Icon = config.icon

  return (
    <div className="flex items-center justify-between py-1.5 px-2 bg-white/5 rounded hover:bg-white/10 transition-colors group">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <Icon
          className={`w-3.5 h-3.5 ${config.color} ${task.status === 'running' ? 'animate-spin' : ''}`}
        />

        <Link
          href={`/developer/projects/${task.project_id}`}
          className="flex-1 min-w-0"
        >
          <span className="text-sm font-medium truncate block hover:underline">
            {task.project_name || `Project ${task.project_id}`}
          </span>
          {task.current_step && (
            <span className="text-xs text-white/50 truncate block">
              {task.current_step}
            </span>
          )}
        </Link>
      </div>

      <div className="flex items-center gap-3">
        {/* Cost */}
        {task.estimated_cost_usd > 0 && (
          <span className="text-xs font-mono text-white/70">
            ${task.estimated_cost_usd.toFixed(4)}
          </span>
        )}

        {/* Progress bar */}
        {task.status === 'running' && task.progress_percent > 0 && (
          <div className="w-16 h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-400 transition-all duration-300"
              style={{ width: `${task.progress_percent}%` }}
            />
          </div>
        )}

        {/* Cancel button */}
        <button
          onClick={() => onCancel(task.task_id)}
          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded transition-all"
          title="Abbrechen"
        >
          <X className="w-3.5 h-3.5 text-red-400" />
        </button>
      </div>
    </div>
  )
}

export default NexusGlobalStatusBar
